import React from 'react';
import { styled } from '@compiled/react';
import { colors } from '@atlaskit/theme';

import { token } from '@atlaskit/tokens';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { NonCriticalErrorBoundary } from '../common/utils/non-critical-error-boundary.tsx';
import { useReportCoreData } from '../services/deployment-frequency-core/index.tsx';
import { BatchSizeChart } from './batch-size-chart/index.tsx';
import { DeploymentFrequencyBarChart } from './deployment-frequency-bar-chart/index.tsx';
import { DeploymentModal } from './deployment-modal/index.tsx';
import Header from './header/index.tsx';
import { IssuesPerDeploymentChart } from './issues-per-deployment-chart/index.tsx';
import { LineChartByEnvironment } from './line-chart-by-environment/index.tsx';
import { PerDeploymentDetailTable } from './per-deployment-detail-table/index.tsx';
import PeriodFilter from './period-filter/index.tsx';
import WelcomeMessage from './welcome-message/index.tsx';

const DeploymentFrequencyReport = () => {
	const { data } = useReportCoreData();

	return (
		<DeploymentFrequencyReportContainer>
			<NonCriticalErrorBoundary fallback="unmount" eventLocation="welcomeMessage">
				<WelcomeMessage />
			</NonCriticalErrorBoundary>
			<Header />
			<PeriodFilter />
			<CoreChartContainer>
				<DeploymentFrequencyBarChart />
				<LineChartByEnvironment />
			</CoreChartContainer>
			{/* We want to render the detail chart container only except no deployment provider state. Loading and error state are handled internally. We don't care about it in this root level. */}
			{!(data && data.hasDeploymentProvider === false) && (
				<DetailChartContainer>
					<IssuesPerDeploymentChart />
					<WeekBreakDownContainer>
						<BatchSizeChart />
						<PerDeploymentDetailTable />
					</WeekBreakDownContainer>
				</DetailChartContainer>
			)}
			<DeploymentModal />
		</DeploymentFrequencyReportContainer>
	);
};

export default DeploymentFrequencyReport;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DeploymentFrequencyReportContainer = styled.div({
	paddingTop: 0,
	paddingRight: token('space.400'),
	paddingBottom: 0,
	paddingLeft: token('space.400'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CoreChartContainer = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `${gridSize * 2}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('elevation.surface.sunken', colors.N10A),
	paddingTop: token('space.300'),
	paddingRight: token('space.150'),
	paddingBottom: token('space.300'),
	paddingLeft: token('space.150'),
	marginTop: token('space.200'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DetailChartContainer = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `${gridSize * 2}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('elevation.surface.sunken', colors.N10A),
	paddingTop: token('space.300'),
	paddingRight: token('space.150'),
	paddingBottom: token('space.300'),
	paddingLeft: token('space.150'),
	marginTop: token('space.200'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const WeekBreakDownContainer = styled.div({
	paddingTop: token('space.200'),
	paddingRight: token('space.200'),
	paddingBottom: token('space.200'),
	paddingLeft: token('space.200'),

	backgroundColor: token('elevation.surface'),
	boxShadow: token(
		'elevation.shadow.raised',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		`0px 1px 1px ${colors.N50A},
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766 */ ''}
        0px 0px 1px ${colors.N60A}`,
	),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `${gridSize * 0.5}px`,
	marginTop: 0,
	marginRight: token('space.200'),
	marginBottom: 0,
	marginLeft: token('space.200'),
});
